import React, { useState } from 'react';
import axios from 'axios';
import { designTokens, styles } from './MainPage.styles';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const SHOP_URL = process.env.REACT_APP_SHOP_URL;

export default function PolicySimulator({ 
  simulatedFacility, setSimulatedFacility, 
  simulatedResult, setSimulatedResult 
}) {
  const [zipCode, setZipCode] = useState('');
  const [facilities, setFacilities] = useState([]);
  const [step, setStep] = useState(1);
  const [uiMessage, setUiMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);

  const fetchFacilities = async () => {
    setUiMessage('');
    if (!zipCode) {
      setUiMessage("Please enter a ZIP code.");
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/facility_rules/get_activated_facilities`, {
        zip_code: zipCode, 
        shop_url: SHOP_URL 
      });
      const found = response.data.facilities || [];
      if (found.length === 0) {
        setUiMessage("No facilities found for this ZIP code.");
      } else {
        setFacilities(found);
        setStep(2);
      }
    } catch (err) {
      setUiMessage("Could not fetch facilities.");
    } finally {
      setIsLoading(false);
    }
  };

  const runRealTest = async () => {
      if (!simulatedFacility) {
        setUiMessage("Please select a facility first.");
        return;
      }
      
      setIsLoading(true);
      try {
        const response = await axios.post(`${API_BASE_URL}/facility_rules/facility_verification`, {
          facility_name: simulatedFacility,
          zip_code: zipCode,
          shop_url: SHOP_URL
        });

        // Update check to match "approved" string from backend
        // console.log('response.data.vendor_status:',response)
        const isApproved = response.data.vendor_status === 'yes';
        const facilityData = response.data.specific_rules[0] || {};
        
        setSimulatedResult({
          passed: isApproved,
          facilityName: simulatedFacility,
          address: facilityData.address || 'Address not provided',
          zipCode: zipCode,
          // Show specific restriction_details if not approved
          reason: isApproved 
            ? 'Compliance Check Passed: Facility is approved for transit.' 
            : (facilityData.restriction_details || 'Packaging restricted: Facility policy prohibits these components.')
        });
        setIsResultModalOpen(true);
      } catch (err) {
        setSimulatedResult({ passed: false, reason: 'Verification engine offline or blocked.' });
        setIsResultModalOpen(true);
      } finally {
        setIsLoading(false);
      }
    };

  return (
    <section id="simulator" style={styles.problemSection}>
      <div style={styles.sectionInner}>
        <div style={{ ...styles.splitLayout, alignItems: 'flex-start' }}>
          
          {/* LEFT COLUMN: Header Text */}
          <div style={{ flex: '1 1 450px', paddingRight: '40px' }}>
            <div style={styles.sectionLabel}>Live Demonstration</div>
            <h2 style={styles.sectionTitle}>Dynamic Policy Interceptor Simulation</h2>
            <p style={{ color: '#94a3b8', fontSize: '15px', lineHeight: '1.6' }}>
              Our interceptor pulls live policy constraints from a centralized logistics database. 
              Use this simulator to verify compliance before finalizing shipment.
            </p>
          </div>

          {/* RIGHT COLUMN: Form Controls */}
          <div style={{ flex: '1 1 500px' }}>
            <div style={{...styles.controlBox, position: 'relative'}}>
              {step === 2 && (
                <button 
                  style={{ position: 'absolute', top: '10px', right: '10px', background: 'transparent', border: '1px solid #475569', color: '#94a3b8', cursor: 'pointer', fontSize: '12px', padding: '4px 8px' }} 
                  onClick={() => { setStep(1); setUiMessage(''); setSimulatedResult(null); setSimulatedFacility(''); }}
                >
                  Reset
                </button>
              )}

              {step === 1 ? (
                <div style={styles.inputGroup}>
                  <label style={styles.labelInput}>Search by ZIP Code *</label>
                  <input style={styles.textInput} value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="e.g. 11223" />
                  <button style={{...styles.submitBtn, marginTop: '10px'}} onClick={fetchFacilities} disabled={isLoading}>
                    {isLoading ? 'Searching...' : 'Search Facilities'}
                  </button>
                </div>
              ) : (
                <div style={styles.inputGroup}>
                  <label style={styles.labelInput}>Select Facility</label>
                  <select style={styles.selectInput} value={simulatedFacility} onChange={(e) => setSimulatedFacility(e.target.value)}>
                    <option value="">-- Select Facility --</option>
                    {facilities.map((f, idx) => <option key={idx} value={f.facility_name}>{f.facility_name}</option>)}
                  </select>
                  <button style={{...styles.submitBtn, marginTop: '15px'}} onClick={runRealTest} disabled={isLoading}>
                    {isLoading ? 'Verifying...' : 'Verify Selection'}
                  </button>
                </div>
              )}
              {uiMessage && <p style={{ color: designTokens.red, fontSize: '13px', marginTop: '8px' }}>{uiMessage}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Result Modal - Centered */}
      {isResultModalOpen && simulatedResult && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ background: 'white', padding: '40px', borderRadius: '16px', maxWidth: '500px', width: '90%', textAlign: 'center', boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }}>
            <h2 style={{ marginTop: 0, color: simulatedResult.passed ? '#22c55e' : '#ef4444' }}>
              {simulatedResult.passed ? 'VERIFICATION PASSED' : 'POLICIES BREACHED'}
            </h2>
            <p style={{ margin: '20px 0', color: '#333' }}>{simulatedResult.reason}</p>
            <div style={{ textAlign: 'left', background: '#f1f5f9', padding: '15px', borderRadius: '8px', fontSize: '13px', color: '#475569' }}>
              <p><strong>Facility:</strong> {simulatedResult.facilityName}</p>
              <p><strong>Address:</strong> {simulatedResult.address}</p>
              <p><strong>ZIP:</strong> {simulatedResult.zipCode}</p>
            </div>
            <button style={{ ...styles.submitBtn, marginTop: '25px', width: '100%' }} onClick={() => setIsResultModalOpen(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </section>
  );
}